import { Card } from "@/components/ui/card";

import { fmtArr } from "../format";

export interface UpsellBreakdownRow {
  label: string;
  /** Optional MRR (or other) sub-value rendered to the left of the headline arr. */
  mrr?: number | null;
  /** Annualised value (× 12 for MRR rows, raw for ARR-native rows). */
  arr: number | null;
}

export interface UpsellData {
  total: number | null;
  /** "vs Mar ($328K)" style comparison. */
  comparisonLabel?: string;
  /** Whether the comparison reads as positive (green) or negative (red). */
  comparisonDirection?: "up" | "down";
  rows: UpsellBreakdownRow[];
  source?: string;
}

export function UpsellCard({
  periodLabel,
  data,
}: {
  periodLabel: string;
  data: UpsellData | null;
}) {
  return (
    <Card className="relative h-full overflow-hidden p-5">
      <div className="absolute left-0 top-0 h-full w-1 bg-warning" />
      <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
        Upsell — {periodLabel}
      </div>
      <div className="mt-2 text-3xl font-extrabold tracking-tight tabular-nums text-warning">
        {data ? fmtArr(data.total, 0) : "—"}
      </div>

      {data?.comparisonLabel && (
        <div className="mt-1">
          <span
            className={
              data.comparisonDirection === "down"
                ? "inline-flex items-center gap-1 rounded-full bg-danger-light px-2 py-0.5 text-[11px] font-semibold text-danger"
                : "inline-flex items-center gap-1 rounded-full bg-success-light px-2 py-0.5 text-[11px] font-semibold text-success"
            }
          >
            {data.comparisonDirection === "down" ? "↓" : "↑"} {data.comparisonLabel}
          </span>
        </div>
      )}

      <div className="mt-3 flex flex-col gap-1.5">
        {(data?.rows ?? PLACEHOLDER_ROWS).map((row, idx) => (
          <div key={idx} className="flex items-baseline justify-between text-xs">
            <span className="text-muted-foreground">{row.label}</span>
            <span className="font-semibold tabular-nums">
              {row.mrr != null && (
                <span className="font-normal text-muted-foreground">
                  {fmtArr(row.mrr, 0)} MRR →{" "}
                </span>
              )}
              {row.arr != null ? fmtArr(row.arr, 1) : "—"}
            </span>
          </div>
        ))}
      </div>

      <div className="mt-3 text-[9px] font-semibold uppercase tracking-wider text-muted-foreground/50">
        {data?.source ?? "Source TBD"}
      </div>
    </Card>
  );
}

const PLACEHOLDER_ROWS: UpsellBreakdownRow[] = [
  { label: "Replacement delta", arr: null },
  { label: "Expansion MRR × 12", arr: null },
  { label: "New logo MRR × 12", arr: null },
];
